// buildpy 로고 — py 아이콘 · buildpy 워드마크 · 둘을 붙인 조합. 경로·이름은 brand-paths.ts 그대로, 색은 토큰 클래스만.
import { BRAND_NAME, BUILD_GLYPHS, Glyph, ICON_BOX, ICON_VIEWBOX, PY_GLYPHS, PY_OFFSET, viewBoxOf, WORDMARK_VIEWBOX } from "./brand-paths";
import { cn } from "./cn";

interface MarkProps {
  className?: string;
  /** 조합 안에서처럼 이름을 다른 요소가 이미 말할 때 — 읽기 프로그램에서 숨긴다. */
  decorative?: boolean;
}

/** default = 밝은·어두운 작업면(foreground + brand) · rail = 늘 어두운 레일·모바일 메뉴(rail-strong + rail-brand). */
export type BrandTone = "default" | "rail";

const TONE: Record<BrandTone, { ink: string; accent: string; chip: string; glyph: string }> = {
  default: { ink: "fill-foreground", accent: "fill-brand", chip: "fill-brand", glyph: "fill-brand-foreground" },
  rail: { ink: "fill-rail-strong", accent: "fill-rail-brand", chip: "fill-rail-brand", glyph: "fill-rail-brand-foreground" },
};

const a11y = (decorative?: boolean) =>
  decorative ? { "aria-hidden": true, focusable: false } : { role: "img", "aria-label": BRAND_NAME };

const Glyphs = ({ glyphs, offset = 0 }: { glyphs: Glyph[]; offset?: number }) =>
  glyphs.map((g) => <path key={g.x} d={g.d} transform={`translate(${g.x + offset} 0)`} />);

/** py 칩 — 브랜드색 둥근 사각 + py. 레일 왼쪽 위·로고 조합에 쓴다. */
export const BrandIcon = ({ className, decorative, tone = "default" }: MarkProps & { tone?: BrandTone }) => (
  <svg className={cn("size-6 shrink-0", className)} viewBox={ICON_VIEWBOX} xmlns="http://www.w3.org/2000/svg" {...a11y(decorative)}>
    <rect className={TONE[tone].chip} height={ICON_BOX.size} rx={ICON_BOX.radius} width={ICON_BOX.size} x={ICON_BOX.x} y={ICON_BOX.y} />
    <g className={TONE[tone].glyph} transform="scale(1,-1)">
      <Glyphs glyphs={PY_GLYPHS} />
    </g>
  </svg>
);

/** buildpy 글자 — build 는 잉크색, py 는 브랜드색. 높이만 정하면 폭은 따라온다. */
export const BrandWordmark = ({ className, decorative, tone = "default" }: MarkProps & { tone?: BrandTone }) => (
  <svg
    className={cn("h-5 w-auto shrink-0", className)}
    viewBox={viewBoxOf(WORDMARK_VIEWBOX)}
    xmlns="http://www.w3.org/2000/svg"
    {...a11y(decorative)}>
    <g transform="scale(1,-1)">
      <g className={TONE[tone].ink}>
        <Glyphs glyphs={BUILD_GLYPHS} />
      </g>
      <g className={TONE[tone].accent}>
        <Glyphs glyphs={PY_GLYPHS} offset={PY_OFFSET} />
      </g>
    </g>
  </svg>
);

const LOGO_SIZE = {
  xs: { gap: "gap-x-1", icon: "size-3.5", word: "h-3" },
  sm: { gap: "gap-x-1.5", icon: "size-5", word: "h-4" },
  md: { gap: "gap-x-2", icon: "size-6", word: "h-5" },
  lg: { gap: "gap-x-2.5", icon: "size-9", word: "h-7" },
} as const;

/** 아이콘 + 워드마크 — 머리줄·로그인처럼 이름이 들어갈 로고 자리. 이름은 묶음 하나로 한 번만 읽힌다. */
export const BrandLogo = ({ className, size = "md", tone = "default" }: { className?: string; size?: keyof typeof LOGO_SIZE; tone?: BrandTone }) => (
  <span aria-label={BRAND_NAME} className={cn("inline-flex shrink-0 items-center", LOGO_SIZE[size].gap, className)} data-testid="brand-logo" role="img">
    <BrandIcon decorative className={LOGO_SIZE[size].icon} tone={tone} />
    <BrandWordmark decorative className={LOGO_SIZE[size].word} tone={tone} />
  </span>
);
